import React, { useState, useRef, useEffect } from "react";
import {
  Upload,
  X,
  Image as ImageIcon,
  AlertCircle,
  Check,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";

const ThumbnailImageUpload = ({
  value,
  onChange,
  maxSize = 5,
  acceptedFormats = ["image/jpeg", "image/jpg", "image/png", "image/webp"],
  previewUrl = null,
  className = "",
}) => {
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(previewUrl || value);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setImagePreview(previewUrl || value);
  }, [value, previewUrl]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFile = (file) => {
    setError(null);

    if (!file.type.startsWith("image/")) {
      const errorMsg = "Please upload an image file";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    if (!acceptedFormats.includes(file.type)) {
      const errorMsg = `Accepted formats: ${acceptedFormats
        .map((f) => f.split("/")[1].toUpperCase())
        .join(", ")}`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    if (file.size > maxSize * 1024 * 1024) {
      const errorMsg = `Image size must be less than ${maxSize}MB`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    return true;
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    if (!validateFile(file)) return;

    try {
      const base64 = await convertToBase64(file);
      setImagePreview(base64);
      onChange(base64);
      toast.success("Thumbnail uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload thumbnail");
      setError("Failed to process image");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setError(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={className}>
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <ImageIcon className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Event Thumbnail</h3>
          <span className="text-red-500">*</span>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Upload a thumbnail image for your event. This will be the main image
          displayed for your event. Recommended size: 1200x675px (16:9 aspect
          ratio)
        </p>

        {imagePreview ? (
          <div className="mb-4">
            <div className="relative inline-block group">
              <div className="overflow-hidden bg-gray-100 rounded-xl border-2 border-gray-200 shadow-lg aspect-video w-full max-w-2xl">
                <img
                  src={imagePreview}
                  alt="Event thumbnail preview"
                  className="object-cover w-full h-full transition-transform group-hover:scale-105"
                />
                <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-xl transition-all duration-200 group-hover:bg-opacity-30">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    className="flex items-center px-4 py-2 space-x-2 text-sm font-medium text-gray-900 bg-white rounded-lg shadow-lg opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-50"
                  >
                    <ImageIcon className="w-4 h-4" />
                    <span>Change Image</span>
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 z-10 p-2 text-white bg-red-500 rounded-full shadow-lg transition-colors hover:bg-red-600"
                title="Remove thumbnail"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="absolute top-2 left-2">
                <span className="inline-flex items-center px-2 py-1 text-xs text-white bg-green-600 rounded-md shadow">
                  <Check className="mr-1 w-3 h-3" />
                  Uploaded
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-4 flex items-center justify-center space-x-2 px-4 py-2.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg border border-purple-200 transition-all font-medium text-sm shadow-sm hover:shadow"
            >
              <Upload className="w-4 h-4" />
              <span>Change Thumbnail</span>
            </button>
          </div>
        ) : (
          <div>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                isDragging
                  ? "bg-purple-50 border-purple-500"
                  : error
                  ? "bg-red-50 border-red-300"
                  : "border-gray-300 hover:border-purple-400 hover:bg-gray-50"
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    error ? "bg-red-100" : "bg-purple-100"
                  }`}
                >
                  {error ? (
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  ) : (
                    <Upload className="w-8 h-8 text-purple-600" />
                  )}
                </div>
                <p
                  className={`text-base font-semibold mb-1 ${
                    error ? "text-red-900" : "text-gray-900"
                  }`}
                >
                  {isDragging
                    ? "Drop your image here"
                    : error
                    ? "Upload Failed"
                    : "Upload Event Thumbnail"}
                </p>
                <p className="mb-4 text-sm text-gray-600">
                  {error || "Drag and drop or click to browse"}
                </p>
                <p className="text-xs text-gray-500">
                  {acceptedFormats
                    .map((f) => f.split("/")[1].toUpperCase())
                    .join(", ")}{" "}
                  up to {maxSize}MB
                </p>
              </div>
            </div>

            <div className="p-3 mt-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-purple-800">
                  <p className="font-semibold">Recommended Specifications:</p>
                  <ul className="mt-1 space-y-0.5 text-xs list-disc list-inside">
                    <li>Resolution: 1200x675px (16:9 aspect ratio)</li>
                    <li>File size: Less than {maxSize}MB</li>
                    <li>Formats: JPG, PNG, or WEBP</li>
                    <li>High quality images work best</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center mt-3 text-xs text-red-600">
            <AlertCircle className="mr-1 w-3 h-3" />
            {error}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(",")}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default ThumbnailImageUpload;
